import { CatchError } from 'Exceptions/exceptions'
import { ENV_PAYMENT_VENDOR_OPTIONS } from 'Main/Constant'
import { paymentVendorStatusExplanation } from 'Main/Constant'
import { paymentVendorStatusType } from 'Main/Constant'
import { EMPTY_PAYMENT_VENDOR_FORM } from 'Main/Constant'
import { isBcaPaymentVendor } from 'Main/paymentVendorTypes'
import { isDokuPaymentVendor } from 'Main/paymentVendorTypes'
import PaymentVendorsService from 'Main/service/PaymentVendorsService'
import { currencyFormat } from 'Main/Utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import swal from 'sweetalert'
import { getRefreshToken } from 'variables/common'
import BasicInput from 'views/components/Input/BasicInput'

function PaymentVendorsDetail () {
  const { id } = useParams()
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const paymentVendorsService = useMemo(() => new PaymentVendorsService(), [])
  const isAdd = !id

  const [loading, setLoading] = useState(!isAdd)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [checkingBalance, setCheckingBalance] = useState(false)
  const [unlocking, setUnlocking] = useState(false)
  const [unlocked, setUnlocked] = useState(isAdd)
  const [password, setPassword] = useState('')

  const [vendor, setVendor] = useState(null) // readonly metadata + balances
  const [form, setForm] = useState(EMPTY_PAYMENT_VENDOR_FORM)

  const [showKeys, setShowKeys] = useState(false)
  const [customFeeType, setCustomFeeType] = useState(false)
  const [manualGenerate, setManualGenerate] = useState(false)

  const setField = (field) => (value) => {
    setForm((f) => ({ ...f, [field]: value }))
  }

  const fetchVendor = useCallback(async () => {
    if (isAdd) return

    try {
      setLoading(true)
      setError(null)
      const res = await paymentVendorsService.getById(id)
      const data = res.data || {}
      setUnlocked(false)
      setPassword('')
      setShowKeys(false)
      setVendor(data)
      setForm({
        name: data.name || '',
        enviroment_type: data.enviroment_type || ENV_PAYMENT_VENDOR_OPTIONS[0],
        description: data.description || '',
        enabled: !!data.enabled,
        is_production: !!data.is_production,
        is_payment_support: !!data.is_payment_support,
        client_id: data.client_id || '',
        client_key: data.client_key || '',
        server_key: data.server_key || '',
        bca_group_code: data.bca_group_code || '',
        api_key: data.api_key || '',
        secret_key: data.secret_key || '',
        auth_code: data.auth_code || '',
        doku_public_key: data.doku_public_key || '',
        merchant_public_key: data.merchant_public_key || '',
        public_key: data.public_key || '',
        private_key: data.private_key || '',
        krmdoku_account_no: data.krmdoku_account_no || '',
        krmdoku_fee_type: data.krmdoku_fee_type || '',
        krmdoku_channel_code: data.krmdoku_channel_code || '',
        krmdoku_type_personal_id: data.krmdoku_type_personal_id || '',
        connection_type: data.connection_type || paymentVendorStatusType[0],
        is_fixed_bill: data.is_fixed_bill || false
      })
      setCustomFeeType(!!data.krmdoku_fee_type)
    } catch (err) {
      const caught = CatchError(err, navigate, refreshToken)
      if (caught !== undefined) setError('Failed to load vendor Gateway.')
    } finally {
      setLoading(false)
    }
  }, [id, isAdd, navigate, paymentVendorsService, refreshToken])

  useEffect(() => {
    fetchVendor()
  }, [fetchVendor])

  const handleCustomFeeTypeToggle = (checked) => {
    setCustomFeeType(checked)
    if (!checked) setField('krmdoku_fee_type')('')
  }

  const handleCheckBalance = async () => {
    if (!vendor?.id) return
    setCheckingBalance(true)
    try {
      const result = await paymentVendorsService.checkBalance(vendor.id)
      await swal('Success', `${form.name}: ${result.message || 'Balances retrieved successfully'}`, 'success')
      await fetchVendor()
    } catch (err) {
      await swal('Failed!', `${form.name}: ${err.message}`, 'warning')
    } finally {
      setCheckingBalance(false)
    }
  }

  const handleUnlock = async (event) => {
    event.preventDefault()
    if (!password) return

    try {
      setUnlocking(true)
      const response = await paymentVendorsService.unlock(id, password)
      const credentials = response?.data || {}
      setForm((current) => ({ ...current, ...credentials }))
      setUnlocked(true)
      await swal('Unlocked', 'credentials are now available.', 'success')
    } catch (err) {
      setUnlocked(false)
      await swal('Unable to unlock', err?.response?.data?.message || 'Password verification failed.', 'error')
    } finally {
      setUnlocking(false)
    }
  }

  const handleSave = () => {
    swal(
      isAdd ? 'Add this Vendor Gateway?' : 'Save changes?',
      "Data can't be undone.",
      'warning',
      {
        buttons: {
          cancel: 'Cancel',
          confirm: { text: isAdd ? 'Add' : 'Update', value: 'confirm' }
        }
      }
    ).then(async (value) => {
      if (value !== 'confirm') return

      try {
        setSaving(true)
        const result = isAdd
          ? await paymentVendorsService.create(form)
          : await paymentVendorsService.update(id, form, password)

        await swal('Success!', result.message || (isAdd ? 'Vendor Gateway Added' : 'Vendor Gateway Updated'), 'success')
        navigate('/dashboard/payment-vendors')
      } catch (err) {
        await swal('Error', err.message, 'error')
      } finally {
        setSaving(false)
      }
    })
  }

  const isBca = isBcaPaymentVendor(form.enviroment_type)

  if (loading) {
    return (
      <div className="page-content">
        <div className="d-flex align-items-center gap-2 py-5 justify-content-center">
          <i className="fa fa-circle-o-notch fa-spin" />
          <span>Loading vendor Gateway...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <a href="/dashboard/payment-vendors" className="btn btn-light text-black mb-3">
          <i className="fa-solid fa-arrow-left" /> Back
        </a>
        <div className="alert alert-danger">
          <i className="fa fa-exclamation-circle me-1" />
          {error}
        </div>
      </div>
    )
  }

  const canEdit = isAdd || unlocked
  const secretType = showKeys ? 'text' : 'password'

  return (
    <>
      <div className="page-heading">
        <div className="row align-items-center">
          <div className="col-12 col-md-6">
            <h3>{isAdd ? 'Add Vendor Gateway' : `${unlocked ? 'Edit' : ''} ${form.name}`}</h3>
            <p className="text-subtitle text-muted mb-0">
              {isAdd ? 'Create a new vendor Gateway' : <>ID: <code>{vendor?.id}</code></>}
            </p>
          </div>
          <div className="col-12 col-md-6 text-md-end mt-2 mt-md-0">
            <a href="/dashboard/payment-vendors" className="btn btn-light text-black">
              <i className="fa-solid fa-arrow-left" /> Back
            </a>
          </div>
        </div>
      </div>

      <div className="page-content">
        {!isAdd && form.is_payment_support && (
          <div className="card bg-dark-info mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1 text-white">Saldo KIrim DOKU</h5>
                </div>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={checkingBalance}
                  onClick={handleCheckBalance}
                >
                  <i className={`fa-solid ${checkingBalance ? 'fa-circle-o-notch fa-spin' : 'fa-wallet'}`} /> Cek Saldo
                </button>
              </div>

              <hr style={{ borderColor: 'rgba(255,255,255,0.3)' }} />

              <div className="row mt-3">
                <div className="col-12 col-md-4">
                  <p className="mb-1 text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>Available Balance</p>
                  <h4 className="text-white mb-0">{currencyFormat(vendor?.krmdoku_aval_balance)}</h4>
                </div>
                <div className="col-12 col-md-4 mt-3 mt-md-0">
                  <p className="mb-1 text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>Held Balance</p>
                  <h4 className="text-white mb-0">{currencyFormat(vendor?.krmdoku_held_balance)}</h4>
                </div>
                <div className="col-12 col-md-4 mt-3 mt-md-0">
                  <p className="mb-1 text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>Limit Balance</p>
                  <h4 className="text-white mb-0">{currencyFormat(vendor?.krmdoku_limit_balance)}</h4>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isAdd && !unlocked && (
          <div className="card border-warning mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0"><i className="fa-solid fa-lock me-2" />Unlock Configs</h5>
            </div>
            <div className="card-body">
              <form className="row g-2 align-items-end" onSubmit={handleUnlock}>
                <div className="col-12 col-md-12">
                  <label className="form-label" htmlFor="payment-vendor-password">Password</label>
                  <input
                    id="payment-vendor-password"
                    type="password"
                    autoComplete="current-password"
                    className="form-control"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button type="submit" className="btn btn-warning w-100" disabled={unlocking || !password}>
                    <i className={`fa-solid ${unlocking ? 'fa-circle-notch fa-spin' : 'fa-unlock-keyhole'} me-2`} />
                    {unlocking ? 'Verifying...' : 'Unlock'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {!isAdd && unlocked && (
          <div className="alert alert-warning d-flex justify-content-between align-items-center gap-3" role="alert">
            <span><i className="fa-solid fa-unlock me-2" />Credentials are decrypted for this editing session.</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-dark"
              onClick={() => {
                setUnlocked(false)
                setPassword('')
                fetchVendor()
              }}
            >
              Lock
            </button>
          </div>
        )}

        <div className="card mb-3">
          <div className="card-header">
            <h5 className="card-title mb-0"><i className="fa-solid fa-circle-info me-1" /> General Info</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12 col-md-12">
                <BasicInput
                  label="Name"
                  value={form.name}
                  disabled={!canEdit}
                  onChange={setField('name')}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Environment Type</label>
                <select
                  className="form-control"
                  value={form.enviroment_type}
                  disabled={!canEdit}
                  onChange={(e) => setField('enviroment_type')(e.target.value)}
                >
                  {ENV_PAYMENT_VENDOR_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Connection Type</label>
                <select
                  className="form-control"
                  value={form.connection_type}
                  disabled={!canEdit}
                  onChange={(e) => setField('connection_type')(e.target.value)}
                >
                  {paymentVendorStatusType.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                  <label className="form-label">({form?.connection_type}) {paymentVendorStatusExplanation(form.connection_type)}</label>
              </div>
              <div className="col-12 mb-2">
                <BasicInput label="Description" type="textarea" value={form.description} disabled={!canEdit} onChange={setField('description')} />
              </div>
            </div>

            <div className="row g-3 mt-2 pt-3 border-top">
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pv_enabled"
                    checked={form.enabled}
                    disabled={!canEdit}
                    onChange={(e) => setField('enabled')(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="pv_enabled">Aktifkan Payment Gateway</label>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pv_is_production"
                    checked={form.is_production}
                    disabled={!canEdit}
                    onChange={(e) => setField('is_production')(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="pv_is_production">Production Environment</label>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pv_is_fixed_bill"
                    checked={form.is_fixed_bill}
                    disabled={!canEdit}
                    onChange={(e) => setField('is_fixed_bill')(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="pv_is_fixed_bill">VA Fixed Bill</label>
                </div>
              </div>
              {
                isDokuPaymentVendor(form.enviroment_type) &&  <div className="col-12 col-sm-6 col-lg-3">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pv_is_payment_support"
                    checked={form.is_payment_support}
                    disabled={!canEdit}
                    onChange={(e) => setField('is_payment_support')(e.target.checked)}
                  />
                  <label
                      className="form-check-label"
                      htmlFor="pv_is_payment_support">
                        Payment Support (Kirim DOKU)
                    </label>
                </div>
              </div>}
              {canEdit && <div className="col-12 col-sm-6 col-lg-3">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pv_show_keys"
                    checked={showKeys}
                    onChange={(e) => setShowKeys(e.target.checked)}
                  />
                  <label className="form-check-label text-danger" htmlFor="pv_show_keys"><strong>Tampilkan Semua Key</strong></label>
                </div>
              </div>}
            </div>
          </div>
        </div>

        {form.is_payment_support && (
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0"><i className="fa-solid fa-money-bill-transfer me-1" /> Kirim DOKU Configs</h5>
            </div>
            <div className="card-body">
              <div className="row g-3 align-items-end">
                {canEdit && (
                  <div className="col-12 col-md-6">
                    <BasicInput label="Kirim Doku Account No" value={form.krmdoku_account_no} onChange={setField('krmdoku_account_no')} />
                  </div>
                )}

                <div className="col-8 col-md-4">
                  <BasicInput
                    label="Kirim Doku Fee Type"
                    value={form.krmdoku_fee_type}
                    disabled={!canEdit || !customFeeType}
                    onChange={setField('krmdoku_fee_type')}
                  />
                </div>
                <div className="col-4 col-md-2 pb-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="pv_custom_fee_type"
                      checked={customFeeType}
                      disabled={!canEdit}
                      onChange={(e) => handleCustomFeeTypeToggle(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="pv_custom_fee_type">Custom Fee-Type</label>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <BasicInput label="Kirim Doku Channel Code" value={form.krmdoku_channel_code} disabled={!canEdit} onChange={setField('krmdoku_channel_code')} />
                </div>
                <div className="col-12 col-md-6">
                  <BasicInput label="Kirim Doku Type Personal ID" value={form.krmdoku_type_personal_id} disabled={!canEdit} onChange={setField('krmdoku_type_personal_id')} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12 col-lg-6">
            <div className="card mb-3">
              <div className="card-header">
                <h5 className="card-title mb-0"><i className="fa-solid fa-key me-1" /> Credentials</h5>
              </div>
              <div className="card-body">
                <BasicInput
                  label="Client ID (Mandatory)"
                  type="textarea"
                  value={form.client_id}
                  disabled={!canEdit}
                  onChange={setField('client_id')}
                />
                {isBca && <>
                  <BasicInput
                    label="BCA Group Code (Mandatory)"
                    type="text"
                    value={form.bca_group_code}
                    disabled={!canEdit}
                    onChange={setField('bca_group_code')}
                  />
                </>
                }
                {canEdit && <>
                  <div className="mt-2">
                    <BasicInput
                      label="Client Key"
                      type={secretType}
                      value={form.client_key}
                      onChange={setField('client_key')}
                    />
                  </div>
                  <div className="mt-2">
                    <BasicInput
                      label="Server Key"
                      type={secretType}
                      value={form.server_key}
                      onChange={setField('server_key')}
                    />
                  </div>
                  <div className="mt-2">
                    <BasicInput
                      label={`${form.enviroment_type} API Key (Mandatory)`}
                      type={secretType}
                      value={form.api_key}
                      onChange={setField('api_key')}
                    />
                  </div>
                  <div className="mt-2">
                    <BasicInput
                      label={`${form.enviroment_type} Secret Key (Mandatory)`}
                      type={secretType}
                      value={form.secret_key}
                      onChange={setField('secret_key')}
                    />
                  </div>
                </>}
                <div className="mt-2">
                  <BasicInput
                    label="Auth Code (Optional)"
                    type="password"
                    value={form.auth_code}
                    disabled={!canEdit}
                    onChange={setField('auth_code')}
                  />
                </div>
              </div>
            </div>
          </div>

          {canEdit && <div className="col-12 col-lg-6">
            <div className="card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0"><i className="fa-solid fa-file-shield me-1" /> Asymmetric Keys</h5>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pv_manual_generate"
                    checked={manualGenerate}
                    disabled={!canEdit}
                    onChange={(e) => setManualGenerate(e.target.checked)}
                  />
                  <label className="form-check-label" style={{ fontSize: '0.85rem' }} htmlFor="pv_manual_generate">Manual Generate</label>
                </div>
              </div>
              <div className="card-body">
                <BasicInput
                  label={`${form.enviroment_type} Public Key (Mandatory)`}
                  type="textarea"
                  value={form.doku_public_key}
                  onChange={setField('doku_public_key')}
                />
                <div className="mt-2">
                  <BasicInput
                    label="Merchant Public Key (Default dari Generated)"
                    type="textarea"
                    value={form.merchant_public_key}
                    onChange={setField('merchant_public_key')}
                  />
                </div>
                <div className="mt-2">
                  <BasicInput
                    label="Public Key OpenSSL"
                    type="textarea"
                    value={form.public_key}
                    disabled={!manualGenerate}
                    onChange={setField('public_key')}
                  />
                </div>
                <div className="mt-2">
                  <BasicInput
                    label="Private Key OpenSSL"
                    type="textarea"
                    value={form.private_key}
                    disabled={!manualGenerate}
                    onChange={setField('private_key')}
                  />
                </div>
              </div>
            </div>
          </div>}
        </div>

        {!isAdd && (
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0"><i className="fa-solid fa-clock-rotate-left me-1" /> Metadata</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-3">
                  <label className="form-label">Creation</label>
                  <input type="text" className="form-control" value={vendor?.creation ? new Date(vendor.creation).toLocaleString() : ''} disabled />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Modified</label>
                  <input type="text" className="form-control" value={vendor?.modified ? new Date(vendor.modified).toLocaleString() : ''} disabled />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Created By</label>
                  <input type="text" className="form-control" value={vendor?.created_by || '-'} disabled />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Modified By</label>
                  <input type="text" className="form-control" value={vendor?.modified_by || '-'} disabled />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mb-3">
          <a href="/dashboard/payment-vendors" className="btn btn-light text-black">Cancel</a>
          {canEdit && (
            <button type="button" className="btn btn-success" disabled={saving} onClick={handleSave}>
              <i className={`fa-solid ${saving ? 'fa-circle-o-notch fa-spin' : 'fa-floppy-disk'}`} />
              &nbsp; {isAdd ? 'Submit' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </>
  )
}

export default PaymentVendorsDetail
